/**
 * The one error type backends throw. Screens can branch on `kind` / `code`
 * and localise by `code`; `message` is developer-facing English.
 */
import type { ProfileFieldProblem } from './api-types.ts';

export type BackendErrorKind =
  /** The request never got an HTTP answer (offline, DNS, CORS, connection reset). */
  | 'network'
  /** No answer within the client timeout. */
  | 'timeout'
  /** The server answered with a non-2xx status. */
  | 'http'
  /** The server answered 2xx with a body we couldn't understand. */
  | 'invalid-response'
  /** The request was refused before sending (client-side validation). */
  | 'invalid-input';

export class BackendError extends Error {
  constructor(
    readonly kind: BackendErrorKind,
    /** ApiErrorCode for `http` errors, otherwise a short code such as "network" or "invalid-profile". */
    readonly code: string,
    message: string,
    /** HTTP status for `http` errors, else null. */
    readonly status: number | null = null,
    /** Field problems for `invalid-profile`. */
    readonly details: readonly ProfileFieldProblem[] = [],
  ) {
    super(message);
    this.name = 'BackendError';
  }

  /** True when retrying later (or playing offline) is the right response: no answer, or a 5xx. */
  get isUnreachable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || (this.kind === 'http' && (this.status ?? 0) >= 500);
  }
}
